import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckSquare, Users, ChevronDown } from 'lucide-react';
import { QuickTasksContent } from './QuickTasksContent';
import { FamilyBondingContent } from './FamilyBondingContent';
import { DailyHubWelcome } from './DailyHubWelcome';
import { MorningReviewDialog } from '@/components/quick-tasks/MorningReviewDialog';
import { DailyIntentPicker, DailyIntent } from '@/components/onboarding/DailyIntentPicker';
import { useQuickTasks } from '@/hooks/useQuickTasks';
import { useFamilyBonding } from '@/hooks/useFamilyBonding';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useIsMobile } from '@/hooks/use-mobile';

interface DailyHubPanelProps {
  onPromoteToGoal: (taskText: string, taskId: string) => void;
  /** When true, hides the inline Together Time pill but keeps overlays/listeners. */
  headless?: boolean;
}

type ActivePanel = 'none' | 'tasks' | 'together';

export function DailyHubPanel({ onPromoteToGoal, headless = false }: DailyHubPanelProps) {
  const [activePanel, setActivePanel] = useState<ActivePanel>('none');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const isMobile = useIsMobile();

  const { value: hasSeenWelcome, setValue: setHasSeenWelcome } = useLocalStorage('daily_hub_welcome_seen', false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (activePanel !== 'none' && !hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, [activePanel, hasSeenWelcome]);

  const handleDismissWelcome = () => {
    setShowWelcome(false);
    setHasSeenWelcome(true);
  };

  const {
    tasks,
    pendingTasks,
    needsMorningReview,
    hasSeenFeature: hasSeenTasks,
    carryForward,
    deleteTask,
    markReviewComplete,
    markFeatureSeen: markTasksFeatureSeen,
    getTask,
    getDaysAgoLabel,
  } = useQuickTasks();

  const {
    completedCount: togetherCompleted,
    totalCount: togetherTotal,
  } = useFamilyBonding();

  useEffect(() => {
    if (needsMorningReview) {
      setShowReview(true);
    }
  }, [needsMorningReview]);

  useEffect(() => {
    if (activePanel !== 'none' && !hasSeenTasks) {
      markTasksFeatureSeen();
    }
  }, [activePanel, hasSeenTasks, markTasksFeatureSeen]);

  // Listen for external "open quick tasks" requests (e.g., hero card CTA)
  useEffect(() => {
    const handler = () => {
      setActivePanel('tasks');
      // Defer to allow panel to mount
      setTimeout(() => {
        const input = document.getElementById('quick-task-input') as HTMLInputElement | null;
        if (input) {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          input.focus({ preventScroll: true });
        }
      }, 250);
    };
    window.addEventListener('open-quick-tasks', handler);
    return () => window.removeEventListener('open-quick-tasks', handler);
  }, []);

  // Listen for hero "Together Time" tap
  useEffect(() => {
    const handler = () => setActivePanel('together');
    window.addEventListener('open-together-time', handler);
    return () => window.removeEventListener('open-together-time', handler);
  }, []);

  const handlePromoteToGoal = (taskId: string) => {
    const task = getTask(taskId);
    if (task) {
      onPromoteToGoal(task.text, taskId);
    }
  };

  const handleReviewPromote = (taskId: string) => {
    const task = pendingTasks.find(t => t.id === taskId);
    if (task) {
      onPromoteToGoal(task.text, taskId);
    }
  };

  const handleReviewComplete = () => {
    markReviewComplete();
    setShowReview(false);
  };

  const incompleteTaskCount = tasks.filter(t => !t.completed).length;
  const isTasksOpen = activePanel === 'tasks';
  const isTogetherOpen = activePanel === 'together';
  const panelTitle = isTasksOpen ? "To-Do List" : "Together Time";

  return (
    <>
      {/* Full-screen mobile panel overlay */}
      <AnimatePresence>
        {activePanel !== 'none' && isMobile && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-16 z-40 bg-background border-t border-border rounded-t-2xl shadow-2xl flex flex-col max-h-[60vh]"
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b rounded-t-2xl ${
              isTasksOpen ? 'bg-primary text-primary-foreground' : 'bg-emerald text-emerald-foreground'
            }`}>
              <div className="flex items-center gap-2">
                {isTasksOpen ? <CheckSquare className="h-5 w-5" /> : <Users className="h-5 w-5" />}
                <h2 className="font-semibold text-lg">{panelTitle}</h2>
              </div>
              <button
                onClick={() => setActivePanel('none')}
                className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                aria-label="Minimize panel"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>

            {/* Welcome overlay */}
            {showWelcome && (
              <DailyHubWelcome onDismiss={handleDismissWelcome} />
            )}

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {isTasksOpen && (
                <QuickTasksContent />
              )}
              {isTogetherOpen && (
                <div className="bg-rose-50/50 dark:bg-rose-950/20 p-3">
                  <FamilyBondingContent />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop floating panel */}
      {/* Inline Daily Hub pills (rendered at top of feed, not fixed) */}
      <div className="w-full">
        <div className="flex flex-col items-stretch gap-2">
          {/* Desktop expanded content panel (hidden on mobile - uses full-screen overlay instead) */}
          <AnimatePresence>
            {activePanel !== 'none' && !isMobile && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-background border rounded-xl shadow-lg overflow-hidden"
              >
                {showWelcome && (
                  <DailyHubWelcome onDismiss={handleDismissWelcome} />
                )}

                <div className="max-h-[28rem] overflow-y-auto">
                  {isTasksOpen && (
                    <QuickTasksContent />
                  )}
                  {isTogetherOpen && (
                    <div className="bg-rose-50/50 dark:bg-rose-950/20 p-3">
                      <FamilyBondingContent />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Two-card row: Daily To-Do  |  Weekly Family Checklist */}
          {!headless && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Daily To-Do card */}
              <button
                onClick={() => {
                  setActivePanel(activePanel === 'tasks' ? 'none' : 'tasks');
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl border shadow-md transition-all ${
                  activePanel === 'tasks'
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'bg-background/95 backdrop-blur-sm border-border hover:bg-muted/50'
                }`}
                style={{ touchAction: 'manipulation' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activePanel === 'tasks' ? 'bg-white/20' : 'bg-gradient-to-br from-teal-400 to-teal-600'
                  }`}>
                    <CheckSquare className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-semibold text-sm truncate">To-Do List</span>
                    <span className={`text-[11px] ${
                      activePanel === 'tasks' ? 'text-white/85' : 'text-muted-foreground'
                    }`}>
                      Daily · tap to open
                    </span>
                  </div>
                </div>
                {activePanel === 'tasks' ? (
                  <ChevronDown className="h-4 w-4 text-white/80 flex-shrink-0" />
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-600 dark:text-teal-300 font-medium flex-shrink-0">
                    Open
                  </span>
                )}
              </button>

              {/* Weekly Family card */}
              <button
                onClick={() => {
                  setActivePanel(activePanel === 'together' ? 'none' : 'together');
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl border shadow-md transition-all ${
                  activePanel === 'together'
                    ? 'bg-rose-500 text-white border-rose-500'
                    : 'bg-background/95 backdrop-blur-sm border-border hover:bg-muted/50'
                }`}
                style={{ touchAction: 'manipulation' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activePanel === 'together' ? 'bg-white/20' : 'bg-gradient-to-br from-rose-400 to-rose-600'
                  }`}>
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-semibold text-sm truncate">Family Happiness Checklist</span>
                    <span className={`text-[11px] ${
                      activePanel === 'together' ? 'text-white/85' : 'text-muted-foreground'
                    }`}>
                      Weekly · {togetherCompleted}/{togetherTotal}
                    </span>
                  </div>
                </div>
                {activePanel === 'together' ? (
                  <ChevronDown className="h-4 w-4 text-white/80 flex-shrink-0" />
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-300 font-medium flex-shrink-0">
                    Tap to log
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <DailyIntentPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(intent: DailyIntent) => setActivePanel(intent)}
        tasksBadge={incompleteTaskCount > 0 ? `${incompleteTaskCount}` : undefined}
        togetherBadge={`${togetherCompleted}/${togetherTotal}`}
      />

      <MorningReviewDialog
        open={showReview}
        onOpenChange={setShowReview}
        tasks={pendingTasks}
        onKeep={carryForward}
        onDelete={deleteTask}
        onPromoteToGoal={handleReviewPromote}
        onComplete={handleReviewComplete}
        getDaysAgoLabel={getDaysAgoLabel}
      />
    </>
  );
}
